import axios, { isAxiosError } from "axios";
import { useAuthStore } from "@/stores/auth";
/**
 * Pull the human-readable error from a backend HTTPException (`{detail: "..."}`)
 * or fall back to a sensible default. Use in catch blocks for action toasts so
 * the admin sees what Proxmox actually said instead of "Action failed".
 */
export function extractApiError(err, fallback) {
    if (isAxiosError(err)) {
        const detail = err.response?.data?.detail;
        if (typeof detail === "string" && detail.length > 0)
            return detail;
        if (err.response?.status)
            return `${fallback} (HTTP ${err.response.status})`;
    }
    return fallback;
}
export const client = axios.create({
    baseURL: "",
    withCredentials: true, // send the refresh cookie
    timeout: 30_000,
});
export function installAuthInterceptor(opts) {
    client.interceptors.request.use((config) => {
        const auth = useAuthStore();
        if (auth.accessToken) {
            config.headers.Authorization = `Bearer ${auth.accessToken}`;
        }
        return config;
    });
    client.interceptors.response.use((response) => response, async (error) => {
        const config = error.config;
        const status = error.response?.status;
        // V1.18: surface 429 as a toast and don't try to retry/refresh.
        if (status === 429) {
            const { useToastStore } = await import("@/stores/toast");
            const toasts = useToastStore();
            const body = error.response?.data;
            const action = body?.detail?.action ?? "request";
            const retry = body?.detail?.retry_after_seconds ?? 0;
            const msg = retry > 0
                ? `Rate limit hit on '${action}'. Try again in ${retry}s.`
                : `Rate limit hit on '${action}'.`;
            toasts.warning(msg);
            return Promise.reject(error);
        }
        // Don't retry the refresh call itself.
        const isRefreshCall = config?.url?.endsWith("/api/v1/auth/refresh");
        if (status !== 401 || !config || config._retried || isRefreshCall) {
            return Promise.reject(error);
        }
        config._retried = true;
        try {
            const refreshRes = await client.post("/api/v1/auth/refresh");
            useAuthStore().setAccessToken(refreshRes.data.access_token);
        }
        catch (refreshErr) {
            useAuthStore().clear();
            opts.onRefreshFailed();
            return Promise.reject(refreshErr);
        }
        // Retry the original request with the new token (the request
        // interceptor will re-attach the Authorization header).
        return client.request(config);
    });
}
